import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';

interface LandingPageProps {
  section?: string;
}

const LandingPage: React.FC<LandingPageProps> = ({ section }) => {
  const { user } = useAuth();
  const [activeSection, setActiveSection] = useState(section || 'home');
  const [openFaqIndex, setOpenFaqIndex] = useState<number | null>(null);

  // Scroll to section on mount if specified (for Google Ads sitelinks)
  useEffect(() => {
    if (section) {
      // Small delay to ensure page is fully rendered
      setTimeout(() => {
        const element = document.getElementById(section);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth' });
          setActiveSection(section);
        }
      }, 100);
    }
  }, [section]);

  if (user) {
    window.location.href = '/dashboard';
    return null;
  }

  // Smooth scroll to section
  const scrollToSection = (sectionId: string) => {
    setActiveSection(sectionId);
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-900 to-gray-800 text-white">
      {/* Custom Animations */}
      <style>{`
        @keyframes shield-spin {
          0% {
            transform: translateZ(0px) rotateY(0deg);
          }
          10% {
            transform: translateZ(80px) rotateY(0deg);
          }
          30% {
            transform: translateZ(80px) rotateY(360deg);
          }
          40% {
            transform: translateZ(0px) rotateY(360deg);
          }
          100% {
            transform: translateZ(0px) rotateY(360deg);
          }
        }
        
        @keyframes opacity-pulse {
          0%, 100% {
            opacity: 1;
          }
          50% {
            opacity: 0.5;
          }
        }
        
        .animate-shield-spin {
          animation: shield-spin 30s ease-in-out infinite, opacity-pulse 3s ease-in-out infinite;
          transform-style: preserve-3d;
        }
      `}</style>
      
      {/* Navigation Bar */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-gray-900/80 backdrop-blur-lg border-b border-gray-800">
        <div className="container mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <img src="/logo512.png" alt="Netlox" className="h-10 w-10" />
              <span className="text-2xl font-bold bg-gradient-to-r from-blue-400 to-purple-500 bg-clip-text text-transparent">
            Netlox Proxies
              </span>
            </div>
            
            <div className="hidden md:flex items-center space-x-8">
              <button 
                onClick={() => scrollToSection('home')}
                className={`hover:text-blue-400 transition-colors ${activeSection === 'home' ? 'text-blue-400' : ''}`}
              >
                Home
              </button>
              <button 
                onClick={() => scrollToSection('features')}
                className={`hover:text-blue-400 transition-colors ${activeSection === 'features' ? 'text-blue-400' : ''}`}
              >
                Features
              </button>
              <button 
                onClick={() => scrollToSection('pricing')}
                className={`hover:text-blue-400 transition-colors ${activeSection === 'pricing' ? 'text-blue-400' : ''}`}
              >
                Pricing
              </button>
              <Link
                to="/login"
                className="px-6 py-2 bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 rounded-lg font-semibold transition-all transform hover:scale-105"
              >
                Sign In
              </Link>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section id="home" className="pt-32 pb-20 px-6">
        <div className="container mx-auto max-w-7xl">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div className="text-left">
              <h1 className="text-5xl md:text-7xl font-bold mb-6 leading-tight">
                Premium Proxies
                <br />
                <span className="bg-gradient-to-r from-blue-400 via-purple-500 to-pink-500 bg-clip-text text-transparent">
                  starting from $0.70/GB
                </span>
          </h1>
          
              <p className="text-xl text-gray-300 mb-8 leading-relaxed">
                Access high-performance residential proxies with 190+ country coverage, 
                clean IP pools, and up to 7 days sticky sessions. Starting from $0.70/GB 
                with no expiry limits.
              </p>
              
              <div className="flex flex-wrap gap-4 mb-8">
                <Link
                  to="/login"
                  className="px-8 py-4 bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 rounded-lg font-semibold text-lg transition-all transform hover:scale-105 shadow-lg shadow-blue-500/50"
                >
                  Get Started
                </Link>
                <button
                  onClick={() => scrollToSection('pricing')}
                  className="px-8 py-4 bg-gray-800 hover:bg-gray-700 rounded-lg font-semibold text-lg transition-all border border-gray-700"
                >
                  View Pricing
                </button>
              </div>

              <div className="flex items-center gap-8 text-sm text-gray-400">
                <div className="flex items-center gap-2">
                  <span className="text-green-400">✓</span> Crypto Payments
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-green-400">✓</span> 99.9% Uptime
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-green-400">✓</span> 24/7 Support
                </div>
              </div>
            </div>

            <div className="relative" style={{ perspective: '1000px' }}>
              <div className="relative z-10 bg-gradient-to-br from-gray-800 to-gray-900 rounded-2xl p-8 border border-gray-700 shadow-2xl">
                <div className="w-64 h-64 mx-auto mb-6" style={{ transformStyle: 'preserve-3d' }}>
                  <img 
                    src="/logo512.png" 
                    alt="Netlox Shield" 
                    className="w-full h-full animate-shield-spin"
                  />
                </div>
                <div className="grid grid-cols-3 gap-4 text-center">
                  <div>
                    <div className="text-3xl font-bold text-blue-400">190+</div>
                    <div className="text-sm text-gray-400">Countries</div>
                  </div>
                  <div>
                    <div className="text-3xl font-bold text-purple-400">99.9%</div>
                    <div className="text-sm text-gray-400">Uptime</div>
                  </div>
                  <div>
                    <div className="text-3xl font-bold text-pink-400">7 Day</div>
                    <div className="text-sm text-gray-400">Sticky</div>
                  </div>
                </div>
              </div>
              
              {/* Floating Elements */}
              <div className="absolute -top-4 -right-4 w-40 h-40 bg-blue-500/70 rounded-full blur-3xl animate-pulse"></div>
              <div className="absolute -bottom-4 -left-4 w-40 h-40 bg-purple-500/70 rounded-full blur-3xl animate-pulse"></div>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-16 px-6 bg-gray-800/50">
        <div className="container mx-auto max-w-7xl">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
            <div>
              <div className="text-4xl font-bold text-blue-400 mb-2">100M+</div>
              <div className="text-gray-400">Clean IPs</div>
            </div>
            <div>
              <div className="text-4xl font-bold text-purple-400 mb-2">190+</div>
              <div className="text-gray-400">Countries</div>
            </div>
            <div>
              <div className="text-4xl font-bold text-pink-400 mb-2">~0.6s</div>
              <div className="text-gray-400">Avg Response</div>
            </div>
            <div>
              <div className="text-4xl font-bold text-green-400 mb-2">$0.70</div>
              <div className="text-gray-400">Per GB</div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="py-20 px-6">
        <div className="container mx-auto max-w-7xl">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold mb-4">
              Why Choose <span className="bg-gradient-to-r from-blue-400 to-purple-500 bg-clip-text text-transparent">Netlox</span>
            </h2>
            <p className="text-xl text-gray-400 max-w-2xl mx-auto">
              Industry-leading features designed for developers, businesses, and enterprise clients
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {/* Feature 1 */}
            <div className="bg-gradient-to-br from-gray-800 to-gray-900 rounded-2xl p-8 border border-gray-700 hover:border-blue-500/50 transition-all hover:transform hover:scale-105">
              <div className="w-12 h-12 mb-4">
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="#60a5fa" opacity="0.8">
                  <path d="M11.24,24a2.262,2.262,0,0,1-.948-.212,2.18,2.18,0,0,1-1.2-2.622L10.653,16H6.975A3,3,0,0,1,4.1,12.131l3.024-10A2.983,2.983,0,0,1,10,0h3.693a2.6,2.6,0,0,1,2.433,3.511L14.443,8H17a3,3,0,0,1,2.483,4.684l-6.4,10.3A2.2,2.2,0,0,1,11.24,24ZM10,2a1,1,0,0,0-.958.71l-3.024,10A1,1,0,0,0,6.975,14H12a1,1,0,0,1,.957,1.29L11.01,21.732a.183.183,0,0,0,.121.241A.188.188,0,0,0,11.4,21.9l6.4-10.3a1,1,0,0,0,.078-1.063A.979.979,0,0,0,17,10H13a1,1,0,0,1-.937-1.351l2.19-5.84A.6.6,0,0,0,13.693,2Z"/>
                </svg>
              </div>
              <h3 className="text-2xl font-bold mb-4">Blazing Fast Speed</h3>
              <p className="text-gray-400 mb-4">
                Experience lightning-fast connections with average response times under ~0.6s. 
                Our optimized infrastructure ensures maximum performance for your operations.
              </p>
              <div className="text-sm text-blue-400 font-semibold">Up to 100 Gbps bandwidth</div>
            </div>

            {/* Feature 2 */}
            <div className="bg-gradient-to-br from-gray-800 to-gray-900 rounded-2xl p-8 border border-gray-700 hover:border-purple-500/50 transition-all hover:transform hover:scale-105">
              <div className="w-12 h-12 mb-4">
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="#c084fc" opacity="0.8">
                  <path d="m17.776,21.334c-.564,1.595-2.085,2.666-3.781,2.666H4.005c-1.17,0-2.279-.51-3.044-1.398-.753-.875-1.087-2.025-.917-3.158.405-2.698,1.885-5.198,4.401-7.444C1.929,9.754.449,7.254.044,4.556c-.17-1.132.164-2.283.917-3.157C1.726.51,2.835,0,4.005,0h9.99c1.696,0,3.217,1.071,3.781,2.666.185.521-.088,1.092-.608,1.276-.521.187-1.092-.088-1.276-.608-.283-.798-1.045-1.334-1.896-1.334H4.005c-.587,0-1.145.257-1.528.703-.378.439-.539.991-.454,1.555.375,2.499,1.922,4.84,4.598,6.958.24.189.38.479.38.784s-.14.595-.38.784c-2.676,2.118-4.223,4.458-4.598,6.957-.085.564.076,1.117.454,1.556.384.446.941.703,1.528.703h9.99c.852,0,1.613-.536,1.896-1.333.185-.521.758-.796,1.276-.608.521.184.793.756.608,1.276Zm-8.15-7.39l.508.405c1.172.932,2.727,2.431,3.474,4.275.125.309.088.659-.098.935-.186.275-.496.441-.829.441h-7.362c-.332,0-.643-.166-.829-.441-.186-.275-.223-.625-.099-.933.746-1.853,2.3-3.346,3.472-4.273l.52-.411c.364-.289.88-.288,1.244.002Zm-.627,2.06c-.631.509-1.371,1.194-1.965,1.996h3.928c-.594-.798-1.332-1.484-1.963-1.996Zm15.001-4.003c0,3.309-2.691,6-6,6s-6-2.691-6-6,2.691-6,6-6,6,2.691,6,6Zm-2,0c0-2.206-1.794-4-4-4s-4,1.794-4,4,1.794,4,4,4,4-1.794,4-4Zm-3-.414v-1.586c0-.552-.447-1-1-1s-1,.448-1,1v2c0,.265.105.52.293.707l1,1c.195.195.451.293.707.293s.512-.098.707-.293c.391-.391.391-1.023,0-1.414l-.707-.707Z"/>
                </svg>
              </div>
              <h3 className="text-2xl font-bold mb-4">Non-Expiring Credits</h3>
              <p className="text-gray-400 mb-4">
                Your purchased bandwidth never expires. Use it at your own pace without 
                worrying about monthly deadlines or wasted credits.
              </p>
              <div className="text-sm text-purple-400 font-semibold">100% money utilization</div>
            </div>

            {/* Feature 3 */}
            <div className="bg-gradient-to-br from-gray-800 to-gray-900 rounded-2xl p-8 border border-gray-700 hover:border-pink-500/50 transition-all hover:transform hover:scale-105">
              <div className="w-12 h-12 mb-4">
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="#f472b6" opacity="0.8">
                  <path d="M19.5,24a1,1,0,0,1-.929-.628l-.844-2.113-2.116-.891a1.007,1.007,0,0,1,.035-1.857l2.088-.791.837-2.092a1.008,1.008,0,0,1,1.858,0l.841,2.1,2.1.841a1.007,1.007,0,0,1,0,1.858l-2.1.841-.841,2.1A1,1,0,0,1,19.5,24ZM10,21a2,2,0,0,1-1.936-1.413L6.45,14.54,1.387,12.846a2.032,2.032,0,0,1,.052-3.871L6.462,7.441,8.154,2.387A1.956,1.956,0,0,1,10.108,1a2,2,0,0,1,1.917,1.439l1.532,5.015,5.03,1.61a2.042,2.042,0,0,1,0,3.872h0l-5.039,1.612-1.612,5.039A2,2,0,0,1,10,21Zm.112-17.977L8.2,8.564a1,1,0,0,1-.656.64L2.023,10.888l5.541,1.917a1,1,0,0,1,.636.643l1.77,5.53,1.83-5.53a1,1,0,0,1,.648-.648l5.53-1.769a.072.072,0,0,0,.02-.009L12.448,9.2a1,1,0,0,1-.652-.661Zm8.17,8.96h0ZM20.5,7a1,1,0,0,1-.97-.757l-.357-1.43L17.74,4.428a1,1,0,0,1,.034-1.94l1.4-.325L19.53.757a1,1,0,0,1,1.94,0l.354,1.418,1.418.355a1,1,0,0,1,0,1.94l-1.418.355L21.47,6.243A1,1,0,0,1,20.5,7Z"/>
                </svg>
              </div>
              <h3 className="text-2xl font-bold mb-4">Clean IP Pool</h3>
              <p className="text-gray-400 mb-4">
                Access to premium, clean residential IPs with high trust scores. 
                Our IPs are carefully maintained to ensure maximum success rates.
              </p>
              <div className="text-sm text-pink-400 font-semibold">99%+ success rate</div>
            </div>

            {/* Feature 4 */}
            <div className="bg-gradient-to-br from-gray-800 to-gray-900 rounded-2xl p-8 border border-gray-700 hover:border-green-500/50 transition-all hover:transform hover:scale-105">
              <div className="w-12 h-12 mb-4">
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="#4ade80" opacity="0.8">
                  <path d="m9.436,14h1.564c0,3-.494,4.892-.805,6h-2.086c.139-.45.511-1.721.729-3.127l-1.01-1.114c.685-.62,1.301-1.38,1.307-1.388l.3-.371ZM24,3v19h-6.015c.193-.577.402-1.245.612-2h3.403V7c-.614,0-1.179-.23-1.63-.616-.299-.256-.668-.384-1.037-.384s-.738.128-1.037.384c-.451.386-1.016.616-1.63.616s-1.179-.23-1.63-.616c-.299-.256-.668-.384-1.037-.384s-.738.128-1.037.384c-.451.386-1.016.616-1.63.616s-1.179-.23-1.63-.616c-.299-.256-.668-.384-1.037-.384s-.738.128-1.037.384c-.451.386-1.016.616-1.63.616V3c0-1.654,1.346-3,3-3h12c1.654,0,3,1.346,3,3Zm-2,0c0-.551-.449-1-1-1h-12c-.551,0-1,.449-1,1v1.065c.219-.041.441-.065.667-.065.856,0,1.687.307,2.338.865.104.089.215.135.328.135s.224-.045.328-.135c.652-.558,1.482-.865,2.338-.865s1.687.307,2.338.865c.104.089.215.135.328.135s.224-.045.328-.135c.652-.558,1.482-.865,2.338-.865s1.687.307,2.338.865c.104.089.215.135.328.135v-2Zm-4.001,7.447c.001,6.683-2.552,12.696-2.661,12.949l-.26.604H0v-1.577c.646-1.189,2.502-6.817,2.577-11.938l.007-.454.491-.416c.991-.822,2.429-.822,3.418,0,.608.504,1.48.504,2.087,0,.99-.821,2.428-.821,3.418,0,.608.504,1.48.504,2.087,0,.99-.821,2.428-.821,3.418,0l.474.351.02.481Zm-2.004.583c-.213-.063-.454-.021-.631.125-1.313,1.092-3.326,1.092-4.641,0-.247-.205-.618-.203-.864,0-1.313,1.092-3.326,1.092-4.641,0-.183-.152-.435-.191-.653-.117-.15,4.361-1.355,8.759-2.185,10.963h11.361c.604-1.595,2.147-6.123,2.254-10.971Z"/>
                </svg>
              </div>
              <h3 className="text-2xl font-bold mb-4">7-Day Sticky Sessions</h3>
              <p className="text-gray-400 mb-4">
                Industry-leading sticky sessions up to 7 days. Maintain the same IP 
                for extended periods, perfect for account management and automation.
              </p>
              <div className="text-sm text-green-400 font-semibold">Up to 168 hours</div>
            </div>

            {/* Feature 5 */}
            <div className="bg-gradient-to-br from-gray-800 to-gray-900 rounded-2xl p-8 border border-gray-700 hover:border-yellow-500/50 transition-all hover:transform hover:scale-105">
              <div className="w-12 h-12 mb-4">
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="#facc15" opacity="0.8">
                  <path d="m12,0C5.383,0,0,5.383,0,12s5.383,12,12,12,12-5.383,12-12S18.617,0,12,0Zm0,22c-5.514,0-10-4.486-10-10,0-1.494.33-2.912.92-4.187l1.666,1.751c.265.278.632.436,1.016.436h1.949c.288,0,.564.114.768.318l.373.373c.198.198.309.466.309.745v.963c0,.385.153.753.425,1.025l1.19,1.19c.247.247.386.582.386.931v2.365c0,.602.488,1.09,1.09,1.09h.213c.425,0,.811-.247.989-.633l2.241-4.856c.267-.578.062-1.263-.477-1.601l-2.562-1.601c-.323-.202-.696-.309-1.077-.309h-1.431c-.311,0-.61-.124-.83-.344l-.592-.592c-.312-.312-.312-.817,0-1.128l.529-.529c.243-.243.614-.303.921-.149l.88.44c.361.181.798.11,1.084-.176l.003-.003c.305-.305.363-.778.141-1.147l-.837-1.396c-.175-.292-.171-.654.014-.94.296-.457.78-1.202,1.316-2.016,3.272.2,6.125,1.98,7.801,4.586l-1.59.917c-.49.282-.733.855-.595,1.403l.627,2.507c.09.36.333.662.665.827l2.373,1.178c-.699,4.835-4.871,8.562-9.896,8.562Z"/>
                </svg>
              </div>
              <h3 className="text-2xl font-bold mb-4">Global Coverage</h3>
              <p className="text-gray-400 mb-4">
                Access residential IPs from 190+ countries with precise city-level targeting. 
                Perfect for geo-restricted content and localized testing.
              </p>
              <div className="text-sm text-yellow-400 font-semibold">City-level precision</div>
            </div>

            {/* Feature 6 */}
            <div className="bg-gradient-to-br from-gray-800 to-gray-900 rounded-2xl p-8 border border-gray-700 hover:border-cyan-500/50 transition-all hover:transform hover:scale-105">
              <div className="w-12 h-12 mb-4">
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="#22d3ee" opacity="0.8">
                  <path d="M16.5,0c-4.206,0-7.5,1.977-7.5,4.5v2.587c-.483-.057-.985-.087-1.5-.087C3.294,7,0,8.977,0,11.5v8c0,2.523,3.294,4.5,7.5,4.5,3.407,0,6.216-1.297,7.16-3.131,.598,.087,1.214,.131,1.84,.131,4.206,0,7.5-1.977,7.5-4.5V4.5c0-2.523-3.294-4.5-7.5-4.5Zm5.5,12.5c0,1.18-2.352,2.5-5.5,2.5-.512,0-1.014-.035-1.5-.103v-1.984c.49,.057,.992,.087,1.5,.087,2.194,0,4.14-.538,5.5-1.411v.911ZM2,14.589c1.36,.873,3.306,1.411,5.5,1.411s4.14-.538,5.5-1.411v.911c0,1.18-2.352,2.5-5.5,2.5s-5.5-1.32-5.5-2.5v-.911Zm20-6.089c0,1.18-2.352,2.5-5.5,2.5-.535,0-1.06-.038-1.566-.112-.193-.887-.8-1.684-1.706-2.323,.984,.28,2.092,.435,3.272,.435,2.194,0,4.14-.538,5.5-1.411v.911Zm-5.5-6.5c3.148,0,5.5,1.32,5.5,2.5s-2.352,2.5-5.5,2.5-5.5-1.32-5.5-2.5,2.352-2.5,5.5-2.5ZM7.5,9c3.148,0,5.5,1.32,5.5,2.5s-2.352,2.5-5.5,2.5-5.5-1.32-5.5-2.5,2.352-2.5,5.5-2.5Zm0,13c-3.148,0-5.5-1.32-5.5-2.5v-.911c1.36,.873,3.306,1.411,5.5,1.411s4.14-.538,5.5-1.411v.911c0,1.18-2.352,2.5-5.5,2.5Zm9-3c-.512,0-1.014-.035-1.5-.103v-1.984c.49,.057,.992,.087,1.5,.087,2.194,0,4.14-.538,5.5-1.411v.911c0,1.18-2.352,2.5-5.5,2.5Z"/>
                </svg>
              </div>
              <h3 className="text-2xl font-bold mb-4">Affordable Even Small</h3>
              <p className="text-gray-400 mb-4">
                No bulk purchase requirements. Get the same competitive $0.90/GB rate 
                even for small purchases. Scale as you grow.
              </p>
              <div className="text-sm text-cyan-400 font-semibold">No minimum purchase</div>
            </div>
          </div>

          {/* Additional Features List */}
          <div className="mt-16 bg-gradient-to-br from-blue-900/20 to-purple-900/20 rounded-2xl p-8 border border-blue-500/30">
            <h3 className="text-2xl font-bold mb-6 text-center">Additional Features</h3>
            <div className="grid md:grid-cols-2 gap-6">
              <div className="flex items-start gap-3">
                <span className="text-green-400 text-xl">✓</span>
                <div>
                  <div className="font-semibold mb-1">HTTP & SOCKS5 Support</div>
                  <div className="text-sm text-gray-400">Both protocols supported for maximum compatibility</div>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <span className="text-green-400 text-xl">✓</span>
                <div>
                  <div className="font-semibold mb-1">Instant Activation</div>
                  <div className="text-sm text-gray-400">Start using proxies immediately after purchase</div>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <span className="text-green-400 text-xl">✓</span>
                <div>
                  <div className="font-semibold mb-1">Unlimited Proxy Connections</div>
                  <div className="text-sm text-gray-400">No port limits - use randomized username patterns for unlimited concurrent connections</div>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <span className="text-green-400 text-xl">✓</span>
                <div>
                  <div className="font-semibold mb-1">24/7 Customer Support</div>
                  <div className="text-sm text-gray-400">Expert support team available round the clock</div>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <span className="text-green-400 text-xl">✓</span>
                <div>
                  <div className="font-semibold mb-1">Usage Dashboard</div>
                  <div className="text-sm text-gray-400">Real-time monitoring and analytics</div>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <span className="text-green-400 text-xl">✓</span>
                <div>
                  <div className="font-semibold mb-1">Auto IP Rotation</div>
                  <div className="text-sm text-gray-400">Flexible rotation intervals from rotating on every request to 7 days sticky IP</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Use Cases Section */}
      <section className="py-20 px-6 bg-gray-800/30">
        <div className="container mx-auto max-w-7xl">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold mb-4">Perfect For Any Use Case</h2>
            <p className="text-xl text-gray-400">Trusted by developers, agencies, and enterprises worldwide</p>
          </div>

          <div className="grid md:grid-cols-4 gap-6">
            <div className="bg-gray-800/50 rounded-xl p-6 border border-gray-700 text-center hover:border-cyan-500/50 transition-all hover:transform hover:scale-105">
              <div className="w-14 h-14 mx-auto mb-4">
                <img src="/shopping-cart.svg" alt="E-commerce" className="w-full h-full" />
              </div>
              <h4 className="font-bold mb-2">E-commerce</h4>
              <p className="text-sm text-gray-400">Sneaker copping, price monitoring, marketing</p>
            </div>
            <div className="bg-gray-800/50 rounded-xl p-6 border border-gray-700 text-center hover:border-purple-500/50 transition-all hover:transform hover:scale-105">
              <div className="w-14 h-14 mx-auto mb-4">
                <img src="/mobile-notch.svg" alt="Social Media" className="w-full h-full" />
              </div>
              <h4 className="font-bold mb-2">Social Media</h4>
              <p className="text-sm text-gray-400">Account management, automation, growth tools</p>
            </div>
            <div className="bg-gray-800/50 rounded-xl p-6 border border-gray-700 text-center hover:border-pink-500/50 transition-all hover:transform hover:scale-105">
              <div className="w-14 h-14 mx-auto mb-4">
                <img src="/search-alt.svg" alt="Web Scraping" className="w-full h-full" />
              </div>
              <h4 className="font-bold mb-2">Web Scraping</h4>
              <p className="text-sm text-gray-400">Data collection, market research, SEO monitoring</p>
            </div>
            <div className="bg-gray-800/50 rounded-xl p-6 border border-gray-700 text-center hover:border-blue-500/50 transition-all hover:transform hover:scale-105">
              <div className="w-14 h-14 mx-auto mb-4">
                <img src="/bullseye-arrow.svg" alt="Ad Verification" className="w-full h-full" />
              </div>
              <h4 className="font-bold mb-2">Ad Verification</h4>
              <p className="text-sm text-gray-400">Campaign monitoring, fraud prevention, compliance</p>
            </div>
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section id="pricing" className="py-20 px-6">
        <div className="container mx-auto max-w-7xl">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold mb-4">
              Simple, Transparent <span className="bg-gradient-to-r from-green-400 to-blue-500 bg-clip-text text-transparent">Pricing</span>
            </h2>
            <p className="text-xl text-gray-400 max-w-2xl mx-auto mb-2">
              All features included in every plan. The more you buy, the less you pay.
            </p>
            <p className="text-sm text-gray-500">
              No hidden fees • No monthly commitments • Pay only for what you use
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-6 max-w-5xl mx-auto mb-16">
            {/* Starter Plan */}
            <div className="bg-gradient-to-br from-gray-800 to-gray-900 rounded-2xl p-6 border border-gray-700 hover:border-blue-500/50 transition-all">
              <div className="text-center">
                <h3 className="text-xl font-bold mb-1">Starter</h3>
                <p className="text-gray-400 text-xs mb-6">Start small, scale anytime</p>
                
                <div className="mb-6">
                  <div className="text-4xl font-bold mb-1">
                    $0.90
                    <span className="text-xl text-gray-400">/GB</span>
                  </div>
                  <p className="text-sm text-gray-400">No minimum</p>
                </div>

                <Link
                  to="/login"
                  className="block w-full py-2.5 bg-gray-700 hover:bg-gray-600 rounded-lg font-semibold text-sm text-center transition-all"
                >
                  Get Started
                </Link>
              </div>
            </div>

            {/* Growth Plan - Featured */}
            <div className="bg-gradient-to-br from-blue-600 to-purple-600 rounded-2xl p-6 transform scale-105 shadow-2xl relative border-2 border-yellow-500/50">
              <div className="absolute -top-3 left-1/2 transform -translate-x-1/2 bg-yellow-500 text-gray-900 px-3 py-0.5 rounded-full text-xs font-bold">
                MOST POPULAR
              </div>
              
              <div className="text-center">
                <h3 className="text-xl font-bold mb-1">Growth</h3>
                <p className="text-blue-100 text-xs mb-6">Best value</p>
                
                <div className="mb-4">
                  <div className="text-4xl font-bold mb-1">
                    $0.80
                    <span className="text-xl text-blue-100">/GB</span>
                  </div>
                  <p className="text-sm text-blue-100">1 TB minimum</p>
                  <p className="text-yellow-300 font-semibold text-xs mt-2">💰 Save 11%</p>
                </div>

                <Link
                  to="/login"
                  className="block w-full py-2.5 bg-white text-purple-600 hover:bg-gray-100 rounded-lg font-semibold text-sm text-center transition-all"
                >
                  Get Started
                </Link>
              </div>
            </div>

            {/* Enterprise Plan */}
            <div className="bg-gradient-to-br from-gray-800 to-gray-900 rounded-2xl p-6 border border-purple-500/50 hover:border-purple-500 transition-all">
              <div className="text-center">
                <h3 className="text-xl font-bold mb-1">Enterprise</h3>
                <p className="text-gray-400 text-xs mb-6">Best price per GB</p>
                
                <div className="mb-6">
                  <div className="text-4xl font-bold mb-1 bg-gradient-to-r from-purple-400 to-pink-500 bg-clip-text text-transparent">
                    $0.70
                    <span className="text-xl text-gray-400">/GB</span>
                  </div>
                  <p className="text-sm text-gray-400">10 TB minimum</p>
                  <p className="text-purple-400 font-semibold text-xs mt-2">💰 Save 22%</p>
          </div>
          
          <Link
            to="/login"
                  className="block w-full py-2.5 bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 rounded-lg font-semibold text-sm text-center transition-all"
          >
            Get Started
          </Link>
              </div>
            </div>
          </div>

          <div className="mt-12">
            <div className="flex justify-center items-center gap-8 mb-4 flex-wrap">
              <div className="flex items-center gap-2">
                <span className="text-green-400 text-lg">✓</span>
                <span className="text-gray-400">All cryptos accepted</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-green-400 text-lg">✓</span>
                <span className="text-gray-400">Instant activation</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-green-400 text-lg">✓</span>
                <span className="text-gray-400">Secure checkout</span>
              </div>
            </div>
            <p className="text-center text-sm text-gray-500">
              Need help? <a href="https://t.me/NetloxProxies" target="_blank" rel="noopener noreferrer" className="text-blue-400 hover:text-blue-300">Contact us on Telegram</a>
            </p>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-20 px-6 bg-gray-800/30">
        <div className="container mx-auto max-w-4xl">
          <div className="text-center mb-12">
            <h2 className="text-4xl md:text-5xl font-bold mb-4">
              Frequently Asked <span className="bg-gradient-to-r from-blue-400 to-purple-400 bg-clip-text text-transparent">Questions</span>
            </h2>
            <p className="text-xl text-gray-400">Everything you need to know about Netlox Proxies</p>
          </div>

          <div className="space-y-3">
            {[
              {
                question: 'How does pricing work?',
                answer: 'You pay only for the bandwidth you purchase. Credits never expire, and you can use them at your own pace. The more you buy upfront, the lower the price per GB - ranging from $0.90/GB (no minimum) to $0.70/GB (10TB minimum).'
              },
              {
                question: 'What payment methods do you accept?',
                answer: 'We accept all major cryptocurrencies including Bitcoin, Ethereum, USDT, and many more through our secure payment processor. Your bandwidth is credited instantly after payment confirmation.'
              },
              {
                question: 'How long can I keep the same IP?',
                answer: 'Our sticky sessions last up to 7 days (168 hours). You can maintain the same IP address for extended periods, perfect for account management and automation tasks. You can also rotate on every request for maximum variety.'
              },
              {
                question: 'Do you have connection limits?',
                answer: "No! Unlike other providers, we don't limit you by ports or concurrent connections. Our username-based system allows unlimited simultaneous connections - just use different randomized username patterns."
              },
              {
                question: 'Which protocols are supported?',
                answer: 'Both HTTP and SOCKS5 protocols are fully supported. Use whichever works best for your application. Both protocols provide the same high-quality residential IPs from our clean pool.'
              },
              {
                question: 'What countries are available?',
                answer: 'We offer residential IPs from 190+ countries with city-level targeting. Perfect for geo-restricted content, localized testing, and global operations. All countries are included in every plan.'
              }
            ].map((faq, index) => (
              <div 
                key={index}
                className="bg-gray-800/50 rounded-lg border border-gray-700/50 overflow-hidden transition-all hover:border-gray-600"
              >
                <button
                  onClick={() => setOpenFaqIndex(openFaqIndex === index ? null : index)}
                  className="w-full px-6 py-4 flex items-center justify-between text-left group"
                >
                  <h3 className="text-base font-semibold text-white group-hover:text-gray-200 transition-colors">
                    {faq.question}
                  </h3>
                  <span className={`text-gray-400 text-sm transition-transform duration-200 ${openFaqIndex === index ? 'rotate-180' : ''}`}>
                    ▼
                  </span>
                </button>
                {openFaqIndex === index && (
                  <div className="px-6 pb-4 pt-1">
                    <p className="text-sm text-gray-400 leading-relaxed">
                      {faq.answer}
                    </p>
                  </div>
                )}
              </div>
            ))}
          </div>

          <div className="mt-12 text-center">
            <p className="text-gray-400 mb-3">Still have questions?</p>
            <a 
              href="https://t.me/NetloxProxies" 
              target="_blank" 
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 px-6 py-3 bg-blue-600 hover:bg-blue-700 rounded-lg font-semibold transition-all"
            >
              <span>💬</span>
              <span>Contact us on Telegram</span>
            </a>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-20 px-6">
        <div className="container mx-auto max-w-4xl text-center">
          <h2 className="text-4xl md:text-5xl font-bold mb-6">
            Ready to Get Started?
          </h2>
          <p className="text-xl text-gray-300 mb-8">
            Join thousands of developers and businesses already using Netlox Proxies
          </p>
          <Link
            to="/login"
            className="inline-block px-12 py-4 bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 rounded-lg font-semibold text-lg transition-all transform hover:scale-105 shadow-lg shadow-blue-500/50"
          >
            Start Using Netlox Today
          </Link>
          <p className="mt-6 text-sm text-gray-500">All cryptos accepted • Instant setup</p>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-8 px-6 border-t border-gray-800">
        <div className="container mx-auto max-w-7xl">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <div className="flex items-center space-x-3">
              <img src="/logo512.png" alt="Netlox" className="h-8 w-8" />
              <span className="text-lg font-bold">Netlox Proxies</span>
            </div>
            <div className="text-gray-400 text-sm">
              © 2026 Netlox Proxies. All rights reserved.
          </div>
        </div>
      </div>
      </footer>
    </div>
  );
};

export default LandingPage; 